import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Badge,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Done as MarkAsReadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '@/services/authService';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'account_approval' | 'balance_change' | 'exchange_rate_update' | 'transaction_status' | 'system_maintenance' | 'card_expiry';
  isRead: boolean;
  metadata?: any;
  createdAt: string;
}

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await api.get('/user/notifications', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setNotifications(response.data.data.notifications);
      } else {
        setError('Failed to fetch notifications');
      }

    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      await api.put(`/user/notifications/${notificationId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setNotifications(notifications.map(notif => 
        notif.id === notificationId 
          ? { ...notif, isRead: true }
          : notif
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      await api.delete(`/user/notifications/${notificationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setNotifications(notifications.filter(notif => notif.id !== notificationId));
      setSuccess('Notification deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'account_approval': return <MarkAsReadIcon sx={{ color: 'success.main' }} />;
      case 'balance_change': return <MarkAsReadIcon sx={{ color: 'info.main' }} />;
      case 'exchange_rate_update': return <MarkAsReadIcon sx={{ color: 'primary.main' }} />;
      case 'transaction_status': return <MarkAsReadIcon sx={{ color: 'warning.main' }} />;
      case 'system_maintenance': return <MarkAsReadIcon sx={{ color: 'error.main' }} />;
      default: return <NotificationsIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'account_approval': return 'success';
      case 'balance_change': return 'info';
      case 'exchange_rate_update': return 'primary';
      case 'transaction_status': return 'warning';
      case 'system_maintenance': return 'error';
      default: return 'info';
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading notifications...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Notification Center
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 2 }}>
              <NotificationsIcon />
            </Badge>
          )}
        </Typography>
        <Button
          variant="contained"
          onClick={fetchNotifications}
          startIcon={<NotificationsIcon />}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Notifications List */}
      <Card>
        <CardContent>
          {notifications.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No notifications found
              </Typography>
            </Box>
          ) : (
            <List>
              {notifications.map((notification) => (
                <ListItem key={notification.id} divider>
                  <Box display="flex" alignItems="center" mr={2}>
                    {getNotificationIcon(notification.type)}
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" component="div">
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Chip label="New" size="small" color="primary" />
                        )}
                        <Chip
                          label={notification.type.replace('_', ' ')}
                          size="small"
                          color={getTypeColor(notification.type) as any}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary" component="div">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          {(() => {
                            try {
                              const date = new Date(notification.createdAt);
                              if (isNaN(date.getTime())) {
                                return 'Invalid Date';
                              }
                              return format(date, 'MMM dd, yyyy HH:mm');
                            } catch (e) {
                              return 'Invalid Date';
                            }
                          })()}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" gap={1}>
                      {!notification.isRead && (
                        <Tooltip title="Mark as read">
                          <IconButton 
                            edge="end" 
                            onClick={() => markAsRead(notification.id)}
                          >
                            <MarkAsReadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton 
                          edge="end" 
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotificationCenter;
